"use client";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useRouter } from "next/navigation";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { registerPageDialogOpener } from "./opener";
import { renderPageDialog } from "./render";
import type { PageDialogSettings } from "./settings";
import "./page-dialog.css";

const STATE_KEY = "rewebPageDialog";

interface Shown {
  href: string;
  settings: PageDialogSettings;
  title: string;
  content: ReactNode | null;
}

const modified = (event: MouseEvent) =>
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

/**
 * Shows a page of the website in a dialog when a button asks for it. The page's own address goes into the history, so
 * Back closes the dialog, Forward opens it again, and a shared or reloaded address shows the page itself. A link to
 * another page closes the dialog and goes there; an anchor scrolls inside it; phone, e-mail and other sites behave as
 * usual. A click outside does not close it once the visitor has typed in a form there.
 */
export default function PageDialogHost() {
  const router = useRouter();
  const [shown, setShown] = useState<Shown | null>(null);
  const [open, setOpen] = useState(false);
  const isOpen = useRef(false);
  const pushed = useRef(false);
  const request = useRef(0);
  const escape = useRef(false);
  const typed = useRef(false);
  const scroller = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const current = useRef<Shown | null>(null);
  current.current = shown;

  const show = useCallback(
    async (href: string, settings: PageDialogSettings) => {
      const id = ++request.current;
      typed.current = false;
      isOpen.current = true;
      setShown({ href, settings, title: "", content: null });
      setOpen(true);
      const result = await renderPageDialog(href, {
        header: settings.header,
        footer: settings.footer,
      }).catch(() => null);
      if (id !== request.current) return;
      if (!result) {
        window.location.replace(href);
        return;
      }
      setShown({
        href,
        settings,
        title: result.title,
        content: result.content,
      });
      scroller.current?.scrollTo(0, 0);
    },
    [],
  );

  const hide = useCallback(() => {
    request.current += 1;
    isOpen.current = false;
    setOpen(false);
  }, []);

  const close = useCallback(() => {
    hide();
    if (pushed.current) {
      pushed.current = false;
      window.history.back();
    }
  }, [hide]);

  const leaveTo = useCallback(
    (path: string) => {
      hide();
      if (pushed.current) {
        pushed.current = false;
        router.replace(path);
      } else router.push(path);
    },
    [hide, router],
  );

  useEffect(
    () =>
      registerPageDialogOpener((href, settings) => {
        const target = new URL(href, window.location.href);
        if (!isOpen.current && target.pathname === window.location.pathname)
          return false;
        const state = { [STATE_KEY]: { href, settings } };
        if (isOpen.current) window.history.replaceState(state, "", href);
        else {
          window.history.pushState(state, "", href);
          pushed.current = true;
        }
        void show(href, settings);
        return true;
      }),
    [show],
  );

  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      const again = event.state?.[STATE_KEY];
      if (again?.href && again.settings) {
        pushed.current = true;
        void show(again.href, again.settings);
      } else if (isOpen.current) {
        pushed.current = false;
        hide();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      escape.current = true;
      window.setTimeout(() => (escape.current = false));
    };
    window.addEventListener("popstate", onPopState);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [show, hide]);

  const panel = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      const onClick = (event: MouseEvent) => {
        if (event.defaultPrevented || modified(event)) return;
        const anchor = (event.target as Element | null)?.closest?.(
          "a[href]",
        ) as HTMLAnchorElement | null;
        if (
          !anchor ||
          anchor.hasAttribute("data-page-dialog") ||
          anchor.hasAttribute("download") ||
          (anchor.target && anchor.target !== "_self")
        )
          return;
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== window.location.origin) return;
        const page = new URL(
          current.current?.href ?? "/",
          window.location.href,
        );
        event.preventDefault();
        event.stopPropagation();
        if (
          url.hash &&
          url.pathname === page.pathname &&
          url.search === page.search
        ) {
          const id = decodeURIComponent(url.hash.slice(1));
          scroller.current
            ?.querySelector(`[id="${CSS.escape(id)}"]`)
            ?.scrollIntoView({ block: "start" });
          return;
        }
        leaveTo(`${url.pathname}${url.search}${url.hash}`);
      };
      node.addEventListener("click", onClick, true);
      return () => node.removeEventListener("click", onClick, true);
    },
    [leaveTo],
  );

  const outsideOrEscape = () => {
    if (!escape.current && typed.current) return;
    close();
  };
  const onInput = (event: FormEvent) => {
    if ((event.target as Element).closest("form")) typed.current = true;
  };

  const settings = shown?.settings;
  return (
    <Dialog
      open={open}
      initialFocus={closeButton}
      onClose={outsideOrEscape}
      className="re-page-dialog"
      data-size={settings?.size}
      data-variant={settings?.variant}
      data-header={settings?.header ? "" : undefined}
    >
      <DialogBackdrop transition className="re-page-dialog-backdrop" />
      <div className="re-page-dialog-frame">
        <DialogPanel
          transition
          ref={panel}
          className="re-page-dialog-panel"
          aria-busy={shown?.content ? undefined : true}
        >
          <DialogTitle className="sr-only">
            {shown?.title || "Indlæser siden"}
          </DialogTitle>
          <div className="re-page-dialog-bar">
            <button
              type="button"
              ref={closeButton}
              className="re-page-dialog-close"
              aria-label="Luk"
              onClick={close}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          <div
            ref={scroller}
            className="re-page-dialog-scroll"
            onInput={onInput}
          >
            {shown?.content ?? (
              <div className="re-page-dialog-loading" role="status">
                <span className="re-page-dialog-spinner" aria-hidden />
                <span className="sr-only">Indlæser siden</span>
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
